import { PERMISSIONS, type PermissionValue } from '@/types/Permissions'
import { usePermissionStore } from '@/stores/permissions'
import { generateCacheKey, customCache as permissionCache } from '@/utils/class'

export function usePermission() {
	const permissionStore = usePermissionStore()

	/**
	 * Check if user can perform an action
	 * TypeScript-safe: Pass PERMISSIONS.WALLET.FUND, not string literal
	 */
	const can = (permissionName: PermissionValue): boolean => {
		return permissionStore.hasPermissionByName(permissionName)
	}

	/**
	 * Check if user can perform ALL actions
	 * TypeScript-safe with array of permission constants
	 */
	const canAll = (permissionNames: readonly PermissionValue[]): boolean => {
		return permissionStore.hasAllPermissions(permissionNames)
	}

	/**
	 * Check if user can perform ANY action
	 * TypeScript-safe with array of permission constants
	 */
	const canAny = (permissionNames: readonly PermissionValue[]): boolean => {
		return permissionStore.hasAnyPermission(permissionNames)
	}

	/**
	 * Get all permissions in a category
	 */
	const getCategoryPermissions = (categoryName: string) => {
		return permissionStore.getPermissionsByCategory(categoryName)
	}

	/**
	 * Get user's permissions
	 */
	const getUserPermissions = () => {
		return permissionStore.userPermissions
	}

	return {
		can,
		canAll,
		canAny,
		getCategoryPermissions,
		getUserPermissions,
		permissionStore,
		PERMISSIONS // Export constants for use
	}
}

export function usePermissionCache() {
	const { can: canPermission, canAll: canAllPermission, canAny: canAnyPermission } = usePermission()

	const can = (permissionName: PermissionValue): boolean => {
		const cacheKey = generateCacheKey('permission', permissionName)
		let result = permissionCache.get(cacheKey)

		if (result === null) {
			result = canPermission(permissionName)
			permissionCache.set(cacheKey, result)
		}

		return result
	}

	const canAny = (permissionNames: PermissionValue[]): boolean => {
		const cacheKey = generateCacheKey('permission:any', permissionNames)
		let result = permissionCache.get(cacheKey)
		if (result === null) {
			result = canAnyPermission(permissionNames)
			permissionCache.set(cacheKey, result)
		}
		return result
	}

	const canAll = (permissionNames: PermissionValue[]): boolean => {
		const cacheKey = generateCacheKey('permission:all', permissionNames)
		let result = permissionCache.get(cacheKey)
		if (result === null) {
			result = canAllPermission(permissionNames)
			permissionCache.set(cacheKey, result)
		}

		return result
	}
	/**
	 * Get cache statistics (for debugging)
	 */
	const getCacheStats = () => {
		return permissionCache.getStats()
	}
	/**
	 * Check how long a cache entry is valid
	 */
	const getCacheRemaining = (permissionName: PermissionValue) => {
		const cacheKey = generateCacheKey('permission', permissionName)
		return permissionCache.getRemaining(cacheKey)
	}

	return {
		can,
		canAny,
		canAll,
		clearCache: () => permissionCache.clear(),
		invalidateCache: (pattern: string) => permissionCache.invalidate(pattern),
		getCacheRemaining
	}
}

interface PermissionCheckOptions {
	requireAll?: boolean
	onDenied?: () => void
}

export function usePermissionCheck() {
	const { can, canAll, canAny } = usePermission()

	/**
	 * Check if user has permission with flexible options
	 */
	const checkPermission = (
		required: PermissionValue | PermissionValue[],
		options: PermissionCheckOptions = {}
	): boolean => {
		if (!required) return true

		const requireAll = options.requireAll ?? false

		if (Array.isArray(required)) {
			return requireAll ? canAll(required) : canAny(required)
		}

		return can(required)
	}

	/**
	 * Require permission or execute fallback
	 */
	const requirePermission = (required: PermissionValue | PermissionValue[], fallback?: () => void): boolean => {
		const hasAccess = checkPermission(required)

		if (!hasAccess && fallback) {
			fallback()
		}

		return hasAccess
	}

	/**
	 * Require permission or throw error
	 */
	const requireOrThrow = (required: PermissionValue | PermissionValue[], message?: string): boolean => {
		const hasAccess = checkPermission(required)

		if (!hasAccess) {
			throw new Error(message || `Permission denied: ${required}`)
		}

		return true
	}

	return {
		checkPermission,
		requirePermission,
		requireOrThrow
	}
}
